/* eslint-disable no-restricted-syntax */
// import { LatLng, icon } from 'leaflet';
import moment from 'moment';
import axios from 'axios';

import shipOnline from '@/assets/images/map/online-ship.svg';
import shipOffline from '@/assets/images/map/offline-ship.svg';
import markerOnline from '@/assets/images/map/online-marker.svg';
import markerOffline from '@/assets/images/map/offline-marker.svg';

import { generateTimestampsInSeconds } from '@/util/date';

import AssetSvc from '@/services/AssetSvc';
import PulseSvc from '@/services/PulseSvc';
import SummarySvc from '@/services/SummarySvc';
import DeviceSvc from '@/services/DeviceSvc';
// eslint-disable-next-line no-unused-vars
import LandmarkSvc from '@/services/LandmarkSvc';

import TugBoatIcon from '@/assets/images/map/tug-boat-icon.svg';
import LandingCraftTransportIcon from '@/assets/images/map/landing-craft-transport-icon.svg';
import headingOnline from '@/assets/images/map/online-heading.svg';
import headingOffline from '@/assets/images/map/offline-heading.svg';

import {
  getAllVisibilityStatuses,
  saveVisibilityStatus,
  saveAllVisibilityStatuses,
} from '@/services/dbService';

const state = {
  assets: [],
  liveTrackingId: null,
  isLiveTrackingActive: false,
};

function convertDecimalToDMS(decimal, isLatitude) {
  const degrees = Math.floor(Math.abs(decimal));
  const minutesDecimal = (Math.abs(decimal) - degrees) * 60;
  const minutes = Math.floor(minutesDecimal);
  const seconds = Math.round((minutesDecimal - minutes) * 60);

  // eslint-disable-next-line no-nested-ternary
  const direction = decimal >= 0
    ? (isLatitude ? 'N' : 'E')
    : (isLatitude ? 'S' : 'W');

  return `${degrees}° ${minutes}' ${seconds}'' ${direction}`;
}

// eslint-disable-next-line no-unused-vars
const getLocation = async ({ lat, long }) => {
  try {
    const reverseGeoCodeUrl = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${long}&key=AIzaSyDowItyYfzjbq6S-7EouF2bj_bZDTGIbH8&language=id`;
    // eslint-disable-next-line no-await-in-loop
    const response = await axios.get(reverseGeoCodeUrl);
    const addressComponent = response.data.results[0] ? response.data.results[0].address_components : [];
    let formattedAddress = '-';

    if (addressComponent[1] && addressComponent[2]) {
      formattedAddress = `${addressComponent[1]?.short_name}, ${addressComponent[2]?.short_name}`;
    } else {
      // formattedAddress = addressComponent[0]?.short_name;
      // eslint-disable-next-line no-await-in-loop
      const fetchLocation = await fetch(`/geonames/findNearbyJSON?lat=${lat}&lng=${long}&username=rgibranz_dev`);
      // eslint-disable-next-line no-await-in-loop
      const location = await fetchLocation.json();
      formattedAddress = location?.geonames[0].toponymName ?? '-';
    }
    return formattedAddress;
  } catch (error) {
    console.error('error', error);
    return '-';
  }
};

const processSummarySeriesData = (summary, firstQueryTimestamp, lastQueryTimestamp) => {
  const timestamps = generateTimestampsInSeconds(firstQueryTimestamp, lastQueryTimestamp, 3600);

  const roundedData = (data) => {
    const rounded = {};
    Object.keys(data || {}).forEach((key) => {
      const roundedKey = Math.round(key / 3600) * 3600;
      rounded[roundedKey] = data[key];
    });
    return rounded;
  };

  const roundedGps = roundedData(summary.gps?.data);
  const roundedRpm = roundedData(summary.rpm?.data);
  const roundedFm = roundedData(summary.flowmeter?.data);
  const roundedAe = roundedData(summary.ae?.data);

  return timestamps.map((timestamp) => {
    const gpsData = roundedGps[timestamp] || {};
    const rpmData = roundedRpm[timestamp] || {};
    const fmData = roundedFm[timestamp] || {};
    const aeData = roundedAe[timestamp] || {};

    const latLong = [gpsData?.additional?.LAST_dlgpLatitude_4, gpsData?.additional?.LAST_dlgpLongitude_5];
    const totalCruise = gpsData?.additional?.SUM_dlgpDistance_3;
    const avgSpeed = gpsData.speed;
    const currentAe = Object.values(aeData).find((v) => v.rpm > 0)?.no + 1;
    const portRpm = rpmData.PORT?.rpm;
    const starboardRpm = rpmData.STARBOARD?.rpm;
    const portInFlowrate = fmData.PORT_Flow;
    const starboardInFlowrate = fmData.STARBOARD_Flow;

    return {
      gpsData,
      rpmData,
      fmData,
      aeData,

      dateTime: moment.unix(timestamp).format('DD-MM-YYYY HH:mm:ss'),
      timestamp,
      latLong,
      heading: gpsData.track,
      totalCruise,
      avgSpeed,
      currentAe,
      portRpm,
      starboardRpm,
      portInFlowrate,
      starboardInFlowrate,
    };
  });
};

const mutations = {
  UPDATE_ASSET_STATUS(state, { id, status }) {
    const asset = state.assets.find((asset) => asset.id === id);
    if (asset) {
      asset.status = status;
    }
  },
  UPDATE_ASSET_VISIBILITY(state, { massId, isVisible }) {
    const asset = state.assets.find((asset) => asset.massId === massId);
    if (asset) {
      asset.isVisible = isVisible;
    }
  },
  UPDATE_ASSET_TRAIL(state, { massId, trail }) {
    const asset = state.assets.find((asset) => asset.massId === massId);
    if (asset) {
      asset.trail = trail;
    }
  },
  SET_ASSETS(state, assets) {
    state.assets = assets;
  },
  SET_LIVE_TRACKING_ID(state, id) {
    state.liveTrackingId = id;
  },
  SET_LIVE_TRACKING_ACTIVE(state, status) {
    state.isLiveTrackingActive = status;
  },
};

const actions = {
  async getAssetsData({ commit }) {
    const res = await AssetSvc.getAssets();
    const data = res?.data?.data ?? [];
    data.forEach((asset) => {
      delete asset.massDevice;
    });
    commit('SET_ASSETS', data);
    return res.data.data;
  },

  async getAssetsDevices({ commit }) {
    let currAssets = Object.values(state.assets);
    const devicePromises = currAssets.map((asset) => {
      const param = {
        findField: 'devcMassId',
        findValue: asset.massId,
        max: 100,
      };
      return DeviceSvc.getDevicesByAssetId(param);
    });

    const massDevices = await Promise.all(devicePromises);
    currAssets = currAssets.map((asset, index) => {
      const deviceData = massDevices[index]?.data?.data || [];
      deviceData.forEach((device) => {
        delete device.devcMass;
      });
      return {
        ...asset,
        devices: deviceData,
      };
    });

    commit('SET_ASSETS', currAssets);
    return currAssets;
  },

  stopLiveTracking({ commit, state }) {
    if (state.liveTrackingId) {
      clearTimeout(state.liveTrackingId);
      commit('SET_LIVE_TRACKING_ID', null);
    }
    commit('SET_LIVE_TRACKING_ACTIVE', false);
    console.log('[stopLiveTracking] Live tracking stopped manually.');
  },

  async getLastData({ commit, state, dispatch }) {
    if (!state.isLiveTrackingActive) {
      commit('SET_LIVE_TRACKING_ACTIVE', true);
    }

    console.log(`[getLastData] Get Last Data at ${new Date().toLocaleString()}`);

    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve) => {
      if (!state.isLiveTrackingActive) {
        resolve();
        return;
      }

      const currTime = Math.floor(Date.now() / 1000);
      const currAssets = Object.values(state.assets);

      const devicePromises = currAssets.map((asset) => {
        const param = {
          findField: 'devcMassId',
          findValue: asset.massId,
        };
        return DeviceSvc.getDevicesByAssetId(param);
      });

      const massDevices = await Promise.all(devicePromises);

      const devices = {
        gps: [],
        ae: [],
        rpm: [],
        flowmeter: [],
        temperature: [],
      };
      const massDevicesMap = new Map();

      massDevices.forEach((massDevice, index) => {
        const data = massDevice.data.data;
        const asset = currAssets[index];
        massDevicesMap.set(asset.massId, data);

        const gpsDevice = data.find((v) => v.devcType === 'gps');
        const aeDevice = data.find((v) => v.devcType === 'ae');
        const rpmDevice = data.find((v) => v.devcType === 'rpm');
        const fmDevices = data.filter((v) => v.devcType === 'flowmeter');
        const temperatureDevices = data.filter((v) => v.devcType === 'temperature');

        if (gpsDevice) devices.gps.push(gpsDevice.devcUniqueId);
        if (aeDevice) devices.ae.push(aeDevice.devcUniqueId);
        if (rpmDevice) devices.rpm.push(rpmDevice.devcUniqueId);
        fmDevices.forEach((fm) => devices.flowmeter.push(fm.devcUniqueId));
        temperatureDevices.forEach((temp) => devices.temperature.push(temp.devcUniqueId));
      });

      const gpsPulseDatas = await PulseSvc.getPulse(devices.gps, 'gps');

      const assetsWithGps = await Promise.all(currAssets.map(async (asset) => {
        const newAsset = { ...asset };
        const assetDevices = massDevicesMap.get(asset.massId) || [];
        newAsset.isVisible = true;
        newAsset.nearestLandmark = null;

        const gpsDevice = assetDevices.find((v) => v.devcType === 'gps');
        if (gpsDevice) {
          const gpsData = gpsPulseDatas.find((d) => d?.deviceId?.toLowerCase() === gpsDevice.devcUniqueId.toLowerCase());
          if (gpsData) {
            const diffTime = parseInt(currTime) - parseInt(gpsData.timestamp);
            newAsset.trail = false;
            newAsset.dotTrail = true;
            newAsset.lastGpsData = gpsData;
            newAsset.lastCoordinate = `${convertDecimalToDMS(gpsData.latitude, true)}, ${convertDecimalToDMS(gpsData.longitude, false)}`;
            // eslint-disable-next-line no-undef
            newAsset.lastLatLng = L.latLng(gpsData.latitude, gpsData.longitude);
            newAsset.dataStatus = diffTime < 600;
            let assetIcon;
            let iconSize = [40, 56];

            switch (asset?.massMaty?.matyName?.trim()?.toLowerCase()) {
              case 'tug boat':
                assetIcon = TugBoatIcon;
                iconSize = [60, 86];
                break;
              case 'landing craft transport':
                assetIcon = LandingCraftTransportIcon;
                iconSize = [70, 86];
                break;
              default:
                assetIcon = diffTime > 600 ? shipOffline : shipOnline;
            }

            // eslint-disable-next-line no-undef
            newAsset.headingIcon = L.icon({
              iconUrl: diffTime > 600 ? headingOffline : headingOnline,
              iconSize: [120, 146],
              className: 'heading-icon',
            });
            // eslint-disable-next-line no-undef
            newAsset.icon = L.icon({
              iconUrl: assetIcon,
              iconSize,
              popupAnchor: [0, -41],
              tooltipAnchor: [-50, 50],
              className: 'ship-icon',
            });
            // eslint-disable-next-line no-undef
            newAsset.markerIcon = L.icon({
              iconUrl: diffTime > 600 ? markerOffline : markerOnline,
              iconSize: [35, 61],
              className: 'marker-icon',
            });

            try {
              const landmarkResponse = await LandmarkSvc.landmarks.nearest({
                latitude: gpsData.latitude,
                longitude: gpsData.longitude,
              });
              const landmarkData = landmarkResponse?.data?.data;
              if (landmarkData?.lndmName) {
                newAsset.nearestLandmark = {
                  name: landmarkData.lndmName,
                  distanceMeters: landmarkData.distanceMeters,
                };
              }
            } catch (error) {
              console.warn('Failed to fetch nearest landmark:', error);
            }
          } else {
            console.warn(`No GPS data found for: ${gpsDevice.devcUniqueId}`);
          }
        }
        return newAsset;
      }));

      commit('SET_ASSETS', assetsWithGps);

      resolve();

      // await new Promise((resolve) => setTimeout(resolve, 5000));

      if (!state.isLiveTrackingActive) return;

      const [aePulseDatas, rpmPulseDatas, fmPulseDatas, temperaturePulseDatas] = await Promise.all([
        PulseSvc.getPulse(devices.ae, 'ae'),
        PulseSvc.getPulse(devices.rpm, 'rpm'),
        PulseSvc.getPulse(devices.flowmeter, 'flowmeter'),
        PulseSvc.getPulse(devices.temperature, 'temperature'),
      ]);

      const finalMappedAssets = assetsWithGps.map((asset) => {
        const newAsset = { ...asset };
        const assetDevices = massDevicesMap.get(asset.massId) || [];

        const aeDevice = assetDevices.find((v) => v.devcType === 'ae');
        if (aeDevice) {
          const aeData = aePulseDatas.filter((d) => d.deviceId === aeDevice.devcUniqueId);
          if (aeData) newAsset.lastAeData = aeData;
        }

        const rpmDevice = assetDevices.find((v) => v.devcType === 'rpm');
        if (rpmDevice) {
          const rpmData = rpmPulseDatas.filter((d) => d.deviceId === rpmDevice.devcUniqueId);
          if (rpmData) newAsset.lastRpmData = rpmData;
        }

        const fmDeviceIds = assetDevices.filter((v) => v.devcType === 'flowmeter').map((v) => v.devcUniqueId);
        if (fmDeviceIds.length > 0) {
          const fmData = fmPulseDatas.filter((d) => fmDeviceIds.includes(d.deviceId));
          if (fmData) newAsset.lastFmData = fmData;
        }

        const temperatureDeviceIds = assetDevices.filter((v) => v.devcType === 'temperature').map((v) => v.devcUniqueId);
        if (temperatureDeviceIds.length > 0) {
          const temperatureData = temperaturePulseDatas.filter((d) => temperatureDeviceIds.includes(d.deviceId));
          if (temperatureData) newAsset.lastTemperatureData = temperatureData;
        }

        return newAsset;
      });

      commit('SET_ASSETS', finalMappedAssets);

      if (state.isLiveTrackingActive) {
        const now = new Date();
        const nextTarget = new Date(now);
        nextTarget.setSeconds(59);
        nextTarget.setMilliseconds(0);
        if (now.getTime() >= nextTarget.getTime()) {
          nextTarget.setMinutes(nextTarget.getMinutes() + 1);
        }

        const delayMs = nextTarget.getTime() - now.getTime();

        console.log(`[getLastData] Next run at ${nextTarget.toLocaleTimeString()} (waiting ${delayMs}ms)`);

        const timeoutId = setTimeout(() => {
          dispatch('getLastData');
        }, delayMs);

        commit('SET_LIVE_TRACKING_ID', timeoutId);
      } else {
        console.log('[getLastData] Live tracking stopped, recursion ended.');
      }
    });
  },

  async fetchSummary({ commit }, {
    start, end, interval, massId,
  }) {
    const assets = Object.values(state.assets);
    const mappedAssets = [];
    const firstQueryTimestamp = moment(start).unix();
    const lastQueryTimestamp = moment(end).unix();

    const [
      aeResponse,
      gpsResponse,
      rpmResponse,
      flowmeterResponse,
      temperatureResponse,
    ] = await Promise.all([
      SummarySvc.getSummary({
        start: firstQueryTimestamp,
        end: lastQueryTimestamp,
        aggregatedUnit: interval.toUpperCase(),
        devcType: ['ae'],
        devcMassId: massId,
        showDetails: true,
      }),
      SummarySvc.getSummary({
        start: firstQueryTimestamp,
        end: lastQueryTimestamp,
        aggregatedUnit: interval.toUpperCase(),
        devcType: ['gps'],
        devcMassId: massId,
        showDetails: true,
      }),
      SummarySvc.getSummary({
        start: firstQueryTimestamp,
        end: lastQueryTimestamp,
        aggregatedUnit: interval.toUpperCase(),
        devcType: ['rpm'],
        devcMassId: massId,
        showDetails: true,
      }),
      SummarySvc.getSummary({
        start: firstQueryTimestamp,
        end: lastQueryTimestamp,
        aggregatedUnit: interval.toUpperCase(),
        devcType: ['flowmeter'],
        devcMassId: massId,
        showDetails: true,
      }),
      SummarySvc.getSummary({
        start: firstQueryTimestamp,
        end: lastQueryTimestamp,
        aggregatedUnit: interval.toUpperCase(),
        devcType: ['temperature'],
        devcMassId: massId,
        showDetails: true,
      }),
    ]);

    const summaryData = {
      ...gpsResponse.data.data,
      ...rpmResponse.data.data,
      ...flowmeterResponse.data.data,
      ...aeResponse.data.data,
      ...temperatureResponse.data.data,
    };
    console.log('[fetchSummary] summaryData', JSON.stringify(summaryData));

    for (const asset of assets) {
      if (asset.massId === massId) {
        const mappedCoordinatesData = processSummarySeriesData(summaryData, firstQueryTimestamp, lastQueryTimestamp);

        asset.coordinates = mappedCoordinatesData.map((coordinate) => coordinate.latLong);
        const lastData = mappedCoordinatesData.pop();

        asset.coordinatesData = mappedCoordinatesData;
        asset.summary = summaryData;

        if (lastData.gpsData?.additional?.LAST_dlgpLatitude_4 && lastData.gpsData?.additional?.LAST_dlgpLongitude_5) {
          asset.lastGpsData = lastData.gpsData;
          // eslint-disable-next-line no-undef
          asset.lastLatLng = L.latLng(lastData.gpsData?.additional?.LAST_dlgpLatitude_4, lastData.gpsData?.additional?.LAST_dlgpLongitude_5);
        }
      }
      mappedAssets.push(asset);
    }
    commit('SET_ASSETS', mappedAssets);
    return mappedAssets;
  },

  async fetchAllLastSummary({ commit }) {
    const assets = Object.values(state.assets);
    let mappedAssets = [];

    const assetPromises = assets.map(async (asset) => {
      console.log('[fetchAllLastSummary] debug fetch summary for', asset.massId);
      console.log('[fetchAllLastSummary] last gps', asset.lastGpsData);
      const lastTimestamp = asset.lastGpsData?.timestamp;
      const firstQueryTimestamp = moment((Math.floor(lastTimestamp / 3600) * 3600) * 1000).subtract(1, 'day').unix();
      const lastQueryTimestamp = moment(lastTimestamp * 1000).unix();

      const summaryResponse = await SummarySvc.getSummary({
        start: firstQueryTimestamp,
        end: lastQueryTimestamp,
        aggregatedUnit: 'HOUR',
        devcType: ['gps', 'rpm', 'flowmeter', 'ae'],
        devcMassId: asset.massId,
        showDetails: true,
      });

      const mappedCoordinatesData = processSummarySeriesData(summaryResponse.data.data, firstQueryTimestamp, lastQueryTimestamp);

      asset.coordinates = mappedCoordinatesData.map((coordinate) => coordinate.latLong);
      mappedCoordinatesData.pop();
      asset.coordinatesData = mappedCoordinatesData;
      asset.summary = summaryResponse.data.data;
      return asset;
    });

    mappedAssets = await Promise.all(assetPromises);

    commit('SET_ASSETS', mappedAssets);
    return mappedAssets;
  },

  async getAllLocationData({ commit }) {
    try {
      const assets = Object.values(state.assets);
      const mappedAssets = [];

      // eslint-disable-next-line no-restricted-syntax
      for (const asset of assets) {
        const lat = asset.lastGpsData.latitude;
        const long = asset.lastGpsData.longitude;
        const reverseGeoCodeUrl = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${long}&key=AIzaSyDowItyYfzjbq6S-7EouF2bj_bZDTGIbH8&language=id`;
        // eslint-disable-next-line no-await-in-loop
        const response = await axios.get(reverseGeoCodeUrl);
        const addressComponent = response.data.results[0] ? response.data.results[0].address_components : [];
        let formattedAddress = '-';

        if (addressComponent[1] && addressComponent[2]) {
          formattedAddress = `${addressComponent[1]?.short_name}, ${addressComponent[2]?.short_name}`;
        } else {
          // formattedAddress = addressComponent[0]?.short_name;
          // eslint-disable-next-line no-await-in-loop
          const fetchLocation = await fetch(`/geonames/findNearbyJSON?lat=${lat}&lng=${long}&username=rgibranz_dev`);
          // eslint-disable-next-line no-await-in-loop
          const location = await fetchLocation.json();
          formattedAddress = location?.geonames[0].toponymName;
        }

        asset.location = formattedAddress;
        mappedAssets.push(asset);
      }

      commit('SET_ASSETS', mappedAssets);
      return mappedAssets;
    } catch (error) {
      console.error('Error fetching reverse geocode:', error);
    }
  },

  updateAssetStatus({ commit }, payload) {
    commit('UPDATE_ASSET_STATUS', payload);
  },

  async getAssetsFromAPI({ dispatch, state }) {
    dispatch('stopLiveTracking');

    await dispatch('getAssetsData');
    await dispatch('getAssetsDevices');
    await dispatch('getLastData');

    const initialAssets = state.assets.filter((asset) => asset.lastGpsData);
    return initialAssets;
  },

  async fetchAndReconcileAssets({ dispatch, commit }) {
    const [apiAssets, visibilityMap] = await Promise.all([
      dispatch('getAssetsFromAPI'),
      getAllVisibilityStatuses(),
    ]);

    const reconciledAssets = apiAssets.map((asset) => {
      if (visibilityMap.has(asset.massId)) {
        asset.isVisible = visibilityMap.get(asset.massId);
      } else {
        asset.isVisible = true;
      }
      return asset;
    });

    commit('SET_ASSETS', reconciledAssets);

    const allStatuses = reconciledAssets.map(({ massId, isVisible }) => ({ massId, isVisible }));
    await saveAllVisibilityStatuses(allStatuses);

    return reconciledAssets;
  },

  async updateAssetVisibility({ commit }, payload) {
    commit('UPDATE_ASSET_VISIBILITY', payload);
    await saveVisibilityStatus(payload.massId, payload.isVisible);
  },

  async setAllAssetsVisibility({ commit, state }, isVisible) {
    commit('SET_ALL_ASSETS_VISIBILITY', isVisible);
    const allStatuses = state.assets.map((asset) => ({
      massId: asset.massId,
      isVisible,
    }));
    await saveAllVisibilityStatuses(allStatuses);
  },

  // eslint-disable-next-line no-unused-vars
  updateAssetTrail({ commit }, { massId, trail }) {
    const assets = Object.values(state.assets);

    assets.map((asset) => {
      if (asset.massId === massId) {
        asset.trail = trail;
      }
      return asset;
    });

    commit('SET_ASSETS', assets);
  },

  updateAssetDotTrail({ commit }, { massId, dotTrail }) {
    const assets = Object.values(state.assets);

    assets.map((asset) => {
      if (asset.massId === massId) {
        asset.dotTrail = dotTrail;
      }
      return asset;
    });

    commit('SET_ASSETS', assets);
  },

  resetAssetsData({ dispatch, commit }) {
    dispatch('stopLiveTracking');
    commit('SET_ASSETS', []);
    return [];
  },
};

const getters = {
  assets: (state) => state.assets,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
